import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const useUnsavedChangesWarning = (hasUnsavedChanges, message = '¿Estás seguro de que quieres salir? Los cambios no guardados se perderán.') => {
  const navigate = useNavigate();
  const [showModalState, setShowModalState] = useState(false);
  const pendingNavigationRef = useRef(null);

  // Manejador para beforeunload (cerrar tab/ventana o recargar página)
  const handleBeforeUnload = useCallback((e) => {
    if (hasUnsavedChanges) {
      e.preventDefault();
      e.returnValue = message;
      return message;
    }
  }, [hasUnsavedChanges, message]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [handleBeforeUnload]);

  // Interceptar clics en NavLink/Link (renderizan como <a>) cuando hay cambios sin guardar
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      // Ignorar enlaces externos, mailto, anclas
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return;

      e.preventDefault();
      e.stopPropagation();
      pendingNavigationRef.current = href;
      setShowModalState(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [hasUnsavedChanges]);

  const handleCancel = useCallback(() => {
    setShowModalState(false);
    pendingNavigationRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    const pending = pendingNavigationRef.current;
    setShowModalState(false);
    pendingNavigationRef.current = null;
    if (pending) {
      navigate(pending);
    }
  }, [navigate]);

  return {
    showModal: showModalState,
    handleCancel,
    handleConfirm,
    message,
  };
};

export default useUnsavedChangesWarning;
